import { useEffect, useState } from "react";
import type {
  DashboardResponse,
  FinanceAccount,
  FinanceAccountInput,
  FinanceAccountType,
  FinanceCategory,
  FinanceFund,
  FinanceFundInput,
  FinanceSummary,
  FinanceTransaction,
  FinanceTransactionInput,
  FinanceTransactionSplitPartInput,
  FinanceTransactionType,
  FinanceTransfer,
  FinanceTransferInput,
  ListResponse,
} from "@atlas/contracts";
import {
  ApiError,
  createFinanceAccount,
  createFinanceFund,
  createFinanceTransaction,
  createFinanceTransfer,
  fetchDashboard,
  fetchFinanceAccounts,
  fetchFinanceCategories,
  fetchFinanceFunds,
  fetchFinanceSummary,
  fetchFinanceTransactions,
  fetchFinanceTransfers,
  splitFinanceTransaction,
  updateFinanceTransaction,
} from "../../lib/api";
import {
  gregorianToJalali,
  jalaliToGregorian,
  jalaliMonthKeyForDate,
  parseJalaliDateKey,
  parseJalaliMonthKey,
  formatJalaliDateLong,
  formatJalaliMonthKey,
  toPersianDigits,
  JALALI_MONTH_NAMES,
} from "@atlas/contracts";
import { formatNumberFa, formatToman } from "../../lib/format";
import { statusLabel } from "../../lib/statusLabels";
import { Icon } from "../Icon";
import { LoadingState, ErrorState, EmptyState } from "../StateViews";
import { Sheet } from "../ui/Sheet";
import { Button } from "../ui/Button";
import { Field, Input } from "../ui/Input";
import { ChipGroup, type ChipOption } from "../ui/Select";
import { Progress } from "../ui/Progress";
import { useToast } from "../ui/Toast";

interface MoreSubPageProps {
  onNavigate: (path: string) => void;
}

function BackHeader({ title, onNavigate }: { title: string; onNavigate: (path: string) => void }) {
  return (
    <header className="page-header" style={{ justifyContent: "flex-start", gap: 10 }}>
      <button type="button" className="icon-btn" onClick={() => onNavigate("/more")} aria-label="بازگشت به بیشتر">
        <Icon name="chevron-right" aria-hidden="true" />
      </button>
      <h1 className="page-header__title">{title}</h1>
    </header>
  );
}

function useDashboard() {
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    fetchDashboard(controller.signal)
      .then(setDashboard)
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "بارگذاری با خطا مواجه شد.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [reloadToken]);

  return { dashboard, loading, error, retry: () => setReloadToken((t) => t + 1) };
}

const ACCOUNT_TYPE_OPTIONS: ChipOption<FinanceAccountType>[] = [
  { label: "بانک", value: "bank" },
  { label: "کارت", value: "card" },
  { label: "نقدی", value: "cash" },
  { label: "سایر", value: "other" },
];

const TRANSACTION_TYPE_OPTIONS: ChipOption<FinanceTransactionType>[] = [
  { label: "هزینه", value: "expense" },
  { label: "درآمد", value: "income" },
];

function currentJalaliMonth(): string {
  return jalaliMonthKeyForDate(new Date());
}

function currentJalaliYear(): number {
  const today = new Date();
  return gregorianToJalali(today.getUTCFullYear(), today.getUTCMonth() + 1, today.getUTCDate()).year;
}

function buildJalaliYearOptions(selectedYear: number): number[] {
  const current = currentJalaliYear();
  const min = Math.min(current - 5, selectedYear);
  const max = Math.max(current + 1, selectedYear);
  const years: number[] = [];
  for (let y = max; y >= min; y--) years.push(y);
  return years;
}

/** Compact two-control Jalali period picker (month + year), used in place of a 12-month chip row. */
function JalaliPeriodPicker({ month, onChange }: { month: string; onChange: (value: string) => void }) {
  const parsed = parseJalaliMonthKey(month) ?? { year: currentJalaliYear(), month: 1 };
  const years = buildJalaliYearOptions(parsed.year);

  return (
    <div className="finance-period-picker" role="group" aria-label="بازه زمانی">
      <select
        className="finance-period-picker__select"
        aria-label="ماه"
        value={parsed.month}
        onChange={(e) => onChange(formatJalaliMonthKey(parsed.year, Number(e.target.value)))}
      >
        {JALALI_MONTH_NAMES.map((name, idx) => (
          <option key={name} value={idx + 1}>
            {name}
          </option>
        ))}
      </select>
      <select
        className="finance-period-picker__select"
        aria-label="سال"
        value={parsed.year}
        onChange={(e) => onChange(formatJalaliMonthKey(Number(e.target.value), parsed.month))}
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {toPersianDigits(y)}
          </option>
        ))}
      </select>
    </div>
  );
}

interface FinanceData {
  summary: FinanceSummary | null;
  accounts: FinanceAccount[] | null;
  funds: FinanceFund[] | null;
  transactions: ListResponse<FinanceTransaction> | null;
  transfers: ListResponse<FinanceTransfer> | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

const FINANCE_TRANSACTIONS_PAGE_SIZE = 100;
const FINANCE_TRANSFERS_PAGE_SIZE = 100;

/** Fetches every transaction for the month, paging past the API's per-request cap so nothing is silently truncated. */
async function fetchAllFinanceTransactions(
  month: string,
  signal: AbortSignal,
): Promise<ListResponse<FinanceTransaction>> {
  let items: FinanceTransaction[] = [];
  let offset = 0;
  let total = 0;
  for (;;) {
    const page = await fetchFinanceTransactions({ month, limit: FINANCE_TRANSACTIONS_PAGE_SIZE, offset }, signal);
    items = items.concat(page.items);
    total = page.total;
    offset += FINANCE_TRANSACTIONS_PAGE_SIZE;
    if (page.items.length < FINANCE_TRANSACTIONS_PAGE_SIZE || items.length >= total) break;
  }
  return { items, total, limit: FINANCE_TRANSACTIONS_PAGE_SIZE, offset: 0 };
}

function useFinanceData(month: string): FinanceData {
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [accounts, setAccounts] = useState<FinanceAccount[] | null>(null);
  const [funds, setFunds] = useState<FinanceFund[] | null>(null);
  const [transactions, setTransactions] = useState<ListResponse<FinanceTransaction> | null>(null);
  const [transfers, setTransfers] = useState<ListResponse<FinanceTransfer> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    Promise.all([
      fetchFinanceSummary(month, controller.signal),
      fetchFinanceAccounts(controller.signal),
      fetchFinanceFunds(month, controller.signal),
      fetchAllFinanceTransactions(month, controller.signal),
      // Transfers are rare (monthly payday moves, occasional reimbursements),
      // so unlike transactions a single page comfortably covers a month.
      fetchFinanceTransfers({ month, limit: FINANCE_TRANSFERS_PAGE_SIZE }, controller.signal),
    ])
      .then(([summaryRes, accountsRes, fundsRes, transactionsRes, transfersRes]) => {
        setSummary(summaryRes);
        setAccounts(accountsRes);
        setFunds(fundsRes);
        setTransactions(transactionsRes);
        setTransfers(transfersRes);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "بارگذاری اطلاعات مالی با خطا مواجه شد.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [month, reloadToken]);

  return { summary, accounts, funds, transactions, transfers, loading, error, refresh: () => setReloadToken((t) => t + 1) };
}

/** Categories aren't month-scoped, so they're fetched once (both kinds) and filtered client-side per transaction type. */
function useFinanceCategories(): FinanceCategory[] | null {
  const [categories, setCategories] = useState<FinanceCategory[] | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    fetchFinanceCategories(undefined, controller.signal)
      .then(setCategories)
      .catch((err) => {
        if (controller.signal.aborted) return;
        if (!(err instanceof ApiError)) throw err;
      });
    return () => controller.abort();
  }, []);

  return categories;
}

interface FinancePageProps extends MoreSubPageProps {
  /** Set by the Quick Add flow to jump straight into the transaction sheet. */
  autoOpenTransaction?: boolean;
  onAutoOpenHandled?: () => void;
}

type FinanceSheet = "account" | "fund" | "transaction" | "editTransaction" | "split" | "transfer" | null;

interface SplitPartState {
  amount: string;
  note: string;
  categoryId: string;
}

/** Sentinel for a split part's category chip meaning "inherit the original
 * transaction's category" -- distinct from "" which explicitly clears it. */
const INHERIT_CATEGORY = "__inherit__";

function buildSplitPartInput(part: SplitPartState): FinanceTransactionSplitPartInput {
  const input: FinanceTransactionSplitPartInput = {
    amountToman: Number(part.amount),
    note: part.note.trim(),
  };
  if (part.categoryId !== INHERIT_CATEGORY) {
    input.categoryId = part.categoryId === "" ? null : part.categoryId;
  }
  return input;
}

const PERSIAN_TO_ASCII_DIGITS: Record<string, string> = {
  "۰": "0",
  "۱": "1",
  "۲": "2",
  "۳": "3",
  "۴": "4",
  "۵": "5",
  "۶": "6",
  "۷": "7",
  "۸": "8",
  "۹": "9",
};

/** Accepts Persian or ASCII digits and either "/" or "-" as the date separator; normalizes to the ASCII "YYYY-MM-DD" key `parseJalaliDateKey` expects. */
function normalizeJalaliDateInput(value: string): string {
  return value
    .trim()
    .replace(/[۰-۹]/g, (digit) => PERSIAN_TO_ASCII_DIGITS[digit] ?? digit)
    .replace(/\//g, "-");
}

export function FinancePage({ onNavigate, autoOpenTransaction, onAutoOpenHandled }: FinancePageProps) {
  const [month, setMonth] = useState(currentJalaliMonth);
  const [financeTab, setFinanceTab] = useState<"accounts" | "transactions" | "funds" | "transfers">("transactions");
  const { summary, accounts, funds, transactions, transfers, loading, error, refresh } = useFinanceData(month);
  const categories = useFinanceCategories();
  const [sheet, setSheet] = useState<FinanceSheet>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const [accountName, setAccountName] = useState("");
  const [accountType, setAccountType] = useState<FinanceAccountType>("bank");

  const [fundName, setFundName] = useState("");
  const [fundCap, setFundCap] = useState("");
  const [fundAccountId, setFundAccountId] = useState("");

  const [txType, setTxType] = useState<FinanceTransactionType>("expense");
  const [txAmount, setTxAmount] = useState("");
  const [txDate, setTxDate] = useState(() => {
    const today = new Date();
    const jalali = gregorianToJalali(today.getUTCFullYear(), today.getUTCMonth() + 1, today.getUTCDate());
    return `${jalali.year}-${String(jalali.month).padStart(2, "0")}-${String(jalali.day).padStart(2, "0")}`;
  });
  const [txAccountId, setTxAccountId] = useState("");
  const [txFundId, setTxFundId] = useState("");
  const [txNote, setTxNote] = useState("");

  const [xferFromAccountId, setXferFromAccountId] = useState("");
  const [xferToAccountId, setXferToAccountId] = useState("");
  const [xferAmount, setXferAmount] = useState("");
  const [xferDate, setXferDate] = useState(() => {
    const today = new Date();
    const jalali = gregorianToJalali(today.getUTCFullYear(), today.getUTCMonth() + 1, today.getUTCDate());
    return `${jalali.year}-${String(jalali.month).padStart(2, "0")}-${String(jalali.day).padStart(2, "0")}`;
  });
  const [xferFundId, setXferFundId] = useState("");
  const [xferNote, setXferNote] = useState("");

  const [selectedTransaction, setSelectedTransaction] = useState<FinanceTransaction | null>(null);
  const [editAmount, setEditAmount] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editAccountId, setEditAccountId] = useState("");
  const [editFundId, setEditFundId] = useState("");
  const [editNote, setEditNote] = useState("");

  const [splitParts, setSplitParts] = useState<SplitPartState[]>([]);

  function getDefaultJalaliDate(): string {
    const today = new Date();
    const jalali = gregorianToJalali(today.getUTCFullYear(), today.getUTCMonth() + 1, today.getUTCDate());
    return `${jalali.year}-${String(jalali.month).padStart(2, "0")}-${String(jalali.day).padStart(2, "0")}`;
  }

  function resetTransactionForm() {
    setTxType("expense");
    setTxAmount("");
    setTxDate(getDefaultJalaliDate());
    setTxAccountId("");
    setTxFundId("");
    setTxNote("");
  }

  useEffect(() => {
    if (!autoOpenTransaction) return;
    resetTransactionForm();
    setSheet("transaction");
    onAutoOpenHandled?.();
    // Only re-run when the Quick Add flow flips the trigger on; reading the
    // latest onAutoOpenHandled/resetTransactionForm via closure is fine here
    // since this effect always consumes and clears the trigger immediately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenTransaction]);

  function openAccountSheet() {
    setAccountName("");
    setAccountType("bank");
    setSheet("account");
  }

  function openFundSheet() {
    setFundName("");
    setFundCap("");
    setFundAccountId("");
    setSheet("fund");
  }

  function openTransactionSheet() {
    resetTransactionForm();
    setSheet("transaction");
  }

  function resetTransferForm() {
    setXferFromAccountId("");
    setXferToAccountId("");
    setXferAmount("");
    setXferDate(getDefaultJalaliDate());
    setXferFundId("");
    setXferNote("");
  }

  function openTransferSheet() {
    resetTransferForm();
    setSheet("transfer");
  }

  function openEditTransactionSheet(tx: FinanceTransaction) {
    const occurredDate = new Date(tx.occurredAt);
    const jalali = gregorianToJalali(
      occurredDate.getUTCFullYear(),
      occurredDate.getUTCMonth() + 1,
      occurredDate.getUTCDate()
    );
    setSelectedTransaction(tx);
    setEditAmount(String(tx.amountToman));
    setEditDate(`${jalali.year}-${String(jalali.month).padStart(2, "0")}-${String(jalali.day).padStart(2, "0")}`);
    setEditAccountId(tx.accountId);
    setEditFundId(tx.fundId ?? "");
    setEditNote(tx.note ?? "");
    setSheet("editTransaction");
  }

  function closeEditTransactionSheet() {
    setSheet(null);
    setSelectedTransaction(null);
  }

  function openSplitSheet() {
    if (!selectedTransaction) return;
    const total = selectedTransaction.amountToman;
    const firstAmount = Math.floor(total / 2);
    const secondAmount = total - firstAmount;
    setSplitParts([
      { amount: String(firstAmount), note: selectedTransaction.note ?? "", categoryId: INHERIT_CATEGORY },
      { amount: String(secondAmount), note: "", categoryId: INHERIT_CATEGORY },
    ]);
    setSheet("split");
  }

  function closeSplitSheet() {
    setSheet(null);
    setSelectedTransaction(null);
    setSplitParts([]);
  }

  function addSplitPart() {
    setSplitParts((parts) => {
      const total = selectedTransaction?.amountToman ?? 0;
      const currentTotal = parts.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
      const remaining = total - currentTotal;
      return [...parts, { amount: remaining > 0 ? String(remaining) : "", note: "", categoryId: INHERIT_CATEGORY }];
    });
  }

  function removeSplitPart(index: number) {
    setSplitParts((parts) => (parts.length > 2 ? parts.filter((_, i) => i !== index) : parts));
  }

  function updateSplitPartAmount(index: number, value: string) {
    setSplitParts((parts) => parts.map((p, i) => (i === index ? { ...p, amount: value } : p)));
  }

  function updateSplitPartNote(index: number, value: string) {
    setSplitParts((parts) => parts.map((p, i) => (i === index ? { ...p, note: value } : p)));
  }

  function updateSplitPartCategory(index: number, value: string) {
    setSplitParts((parts) => parts.map((p, i) => (i === index ? { ...p, categoryId: value } : p)));
  }

  const accountNameValid = accountName.trim().length > 0;

  async function submitAccount() {
    if (!accountNameValid) return;
    setBusy(true);
    try {
      const input: FinanceAccountInput = { name: accountName.trim(), type: accountType };
      await createFinanceAccount(input);
      setSheet(null);
      toast.push("حساب ایجاد شد.");
      refresh();
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "ایجاد حساب با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  const fundNameValid = fundName.trim().length > 0;
  const fundCapNumber = fundCap.trim() ? Number(fundCap) : undefined;
  const fundCapValid = fundCap.trim() === "" || (Number.isInteger(fundCapNumber) && (fundCapNumber as number) > 0);

  async function submitFund() {
    if (!fundNameValid || !fundCapValid) return;
    setBusy(true);
    try {
      const input: FinanceFundInput = {
        name: fundName.trim(),
        monthlyCapToman: fundCapNumber,
        accountId: fundAccountId || undefined,
      };
      await createFinanceFund(input);
      setSheet(null);
      toast.push("صندوق ایجاد شد.");
      refresh();
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "ایجاد صندوق با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  const txAmountNumber = Number(txAmount);
  const txAmountValid = txAmount.trim() !== "" && Number.isInteger(txAmountNumber) && txAmountNumber > 0;

  function parseTxDate(): { year: number; month: number; day: number } | null {
    const normalized = txDate
      .trim()
      .replace(/۰/g, "0")
      .replace(/۱/g, "1")
      .replace(/۲/g, "2")
      .replace(/۳/g, "3")
      .replace(/۴/g, "4")
      .replace(/۵/g, "5")
      .replace(/۶/g, "6")
      .replace(/۷/g, "7")
      .replace(/۸/g, "8")
      .replace(/۹/g, "9")
      .replace(/\//g, "-");
    return parseJalaliDateKey(normalized);
  }

  const txDateValid = parseTxDate() !== null;
  const txNoteValid = txNote.trim().length > 0;
  const txValid = txAmountValid && txAccountId !== "" && txDateValid && txNoteValid;
  /** Only offer funds owned by the selected account (or unassigned funds), since a fund tied to another account can't be tagged here. */
  const txEligibleFunds = (funds ?? []).filter((f) => f.accountId === undefined || f.accountId === txAccountId);

  async function submitTransaction() {
    if (!txValid) return;
    setBusy(true);
    try {
      const jalaliDate = parseTxDate();
      if (!jalaliDate) {
        toast.push("تاریخ نامعتبر است.", "danger");
        setBusy(false);
        return;
      }
      const gregorian = jalaliToGregorian(jalaliDate.year, jalaliDate.month, jalaliDate.day);
      const occurredAt = new Date(Date.UTC(gregorian.year, gregorian.month - 1, gregorian.day)).toISOString();
      const input: FinanceTransactionInput = {
        type: txType,
        amountToman: txAmountNumber,
        occurredAt,
        accountId: txAccountId,
        fundId: txFundId || undefined,
        note: txNote.trim() || undefined,
      };
      await createFinanceTransaction(input);
      setSheet(null);
      toast.push("تراکنش ثبت شد.");
      refresh();
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "ثبت تراکنش با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  const xferAmountNumber = Number(xferAmount);
  const xferAmountValid = xferAmount.trim() !== "" && Number.isInteger(xferAmountNumber) && xferAmountNumber > 0;

  function parseXferDate(): { year: number; month: number; day: number } | null {
    return parseJalaliDateKey(normalizeJalaliDateInput(xferDate));
  }

  const xferDateValid = parseXferDate() !== null;
  const xferNoteValid = xferNote.trim().length > 0;
  const xferAccountsValid = xferFromAccountId !== "" && xferToAccountId !== "" && xferFromAccountId !== xferToAccountId;
  const xferValid = xferAmountValid && xferAccountsValid && xferDateValid && xferNoteValid;
  /** Only offer funds that belong to one of the two accounts in the transfer, since a fund tag only makes sense there. */
  const xferEligibleFunds = (funds ?? []).filter(
    (f) => f.accountId === xferFromAccountId || f.accountId === xferToAccountId,
  );

  async function submitTransfer() {
    if (!xferValid) return;
    setBusy(true);
    try {
      const jalaliDate = parseXferDate();
      if (!jalaliDate) {
        toast.push("تاریخ نامعتبر است.", "danger");
        setBusy(false);
        return;
      }
      const gregorian = jalaliToGregorian(jalaliDate.year, jalaliDate.month, jalaliDate.day);
      const occurredAt = new Date(Date.UTC(gregorian.year, gregorian.month - 1, gregorian.day)).toISOString();
      const input: FinanceTransferInput = {
        fromAccountId: xferFromAccountId,
        toAccountId: xferToAccountId,
        amountToman: xferAmountNumber,
        occurredAt,
        note: xferNote.trim(),
        fundId: xferFundId || undefined,
      };
      await createFinanceTransfer(input);
      setSheet(null);
      toast.push("انتقال ثبت شد.");
      refresh();
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "ثبت انتقال با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  const editAmountNumber = Number(editAmount);
  const editAmountValid = editAmount.trim() !== "" && Number.isInteger(editAmountNumber) && editAmountNumber > 0;

  function parseEditDate(): { year: number; month: number; day: number } | null {
    return parseJalaliDateKey(normalizeJalaliDateInput(editDate));
  }

  const editDateValid = parseEditDate() !== null;
  const editNoteValid = editNote.trim().length > 0;
  const editValid = editAmountValid && editAccountId !== "" && editDateValid && editNoteValid;
  /** Only offer funds owned by the selected account (or unassigned funds), since a fund tied to another account can't be tagged here. */
  const editEligibleFunds = (funds ?? []).filter((f) => f.accountId === undefined || f.accountId === editAccountId);

  async function submitEditTransaction() {
    if (!selectedTransaction || !editValid) return;
    setBusy(true);
    try {
      const jalaliDate = parseEditDate();
      if (!jalaliDate) {
        toast.push("تاریخ نامعتبر است.", "danger");
        setBusy(false);
        return;
      }
      const gregorian = jalaliToGregorian(jalaliDate.year, jalaliDate.month, jalaliDate.day);
      const occurredAt = new Date(Date.UTC(gregorian.year, gregorian.month - 1, gregorian.day)).toISOString();
      await updateFinanceTransaction(selectedTransaction.id, {
        amountToman: editAmountNumber,
        occurredAt,
        accountId: editAccountId,
        fundId: editFundId || null,
        note: editNote.trim(),
      });
      closeEditTransactionSheet();
      toast.push("تراکنش به‌روزرسانی شد.");
      refresh();
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "به‌روزرسانی تراکنش با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  const splitOriginalTotal = selectedTransaction?.amountToman ?? 0;
  const splitCurrentTotal = splitParts.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const splitRemaining = splitOriginalTotal - splitCurrentTotal;
  const splitAmountsValid = splitParts.every((p) => {
    const n = Number(p.amount);
    return p.amount.trim() !== "" && Number.isInteger(n) && n > 0;
  });
  const splitNotesValid = splitParts.every((p) => p.note.trim().length > 0);
  const splitValid = splitParts.length >= 2 && splitAmountsValid && splitNotesValid && splitRemaining === 0;
  const splitCategories = (categories ?? []).filter((c) => c.kind === selectedTransaction?.type);

  async function submitSplit() {
    if (!selectedTransaction || !splitValid) return;
    setBusy(true);
    try {
      const parts = splitParts.map(buildSplitPartInput);
      await splitFinanceTransaction(selectedTransaction.id, { parts });
      closeSplitSheet();
      toast.push("تراکنش با موفقیت خرد شد.");
      refresh();
    } catch (err) {
      toast.push(err instanceof ApiError ? err.message : "خرد کردن تراکنش با خطا مواجه شد.", "danger");
    } finally {
      setBusy(false);
    }
  }

  const hasAccounts = Boolean(accounts && accounts.length > 0);
  const hasAnyData = Boolean(
    summary && (summary.transactionCount > 0 || accounts?.length || funds?.length || transactions?.items.length),
  );

  return (
    <section className="finance-page">
      <BackHeader title="مالی" onNavigate={onNavigate} />

      <JalaliPeriodPicker month={month} onChange={setMonth} />

      {loading && !summary && <LoadingState />}
      {error && <ErrorState message={error} onRetry={refresh} />}

      {!loading && !error && (
        <div className="finance-page__stack">
          {summary && (
            <div className="card" style={{ marginBottom: 16 }}>
              <p className="summary-label">خالص این ماه</p>
              <p className="summary-value">{formatToman(summary.netToman)}</p>
              <div className="finance-tx-row" style={{ marginTop: 12 }}>
                <span className="finance-tx-row__title">درآمد</span>
                <span className="finance-tx-row__amount finance-tx-row__amount--in">
                  {formatToman(summary.incomeToman)}
                </span>
              </div>
              <div className="finance-tx-row" style={{ marginTop: 8 }}>
                <span className="finance-tx-row__title">هزینه</span>
                <span className="finance-tx-row__amount finance-tx-row__amount--out">
                  {formatToman(summary.expenseToman)}
                </span>
              </div>
              <p className="state-block__hint" style={{ marginTop: 8 }}>
                {formatNumberFa(summary.transactionCount)} تراکنش
              </p>
            </div>
          )}

          {!hasAnyData && (
            <EmptyState message="هنوز اطلاعات مالی برای این ماه ثبت نشده است." hint="از دکمه‌های زیر شروع کنید." />
          )}

          <div className="finance-tabs" role="tablist" aria-label="بخش مالی">
            <button
              type="button"
              role="tab"
              className="finance-tabs__tab"
              aria-selected={financeTab === "accounts"}
              data-active={financeTab === "accounts"}
              onClick={() => setFinanceTab("accounts")}
            >
              حساب‌ها
            </button>
            <button
              type="button"
              role="tab"
              className="finance-tabs__tab"
              aria-selected={financeTab === "transactions"}
              data-active={financeTab === "transactions"}
              onClick={() => setFinanceTab("transactions")}
            >
              تراکنش‌ها
            </button>
            <button
              type="button"
              role="tab"
              className="finance-tabs__tab"
              aria-selected={financeTab === "funds"}
              data-active={financeTab === "funds"}
              onClick={() => setFinanceTab("funds")}
            >
              صندوق‌ها
            </button>
            <button
              type="button"
              role="tab"
              className="finance-tabs__tab"
              aria-selected={financeTab === "transfers"}
              data-active={financeTab === "transfers"}
              onClick={() => setFinanceTab("transfers")}
            >
              انتقال‌ها
            </button>
          </div>

          {financeTab === "accounts" && (
            <section className="section">
              <div className="finance-page__section-header">
                <p className="section__label">حساب‌ها</p>
                <Button variant="secondary" size="sm" onClick={openAccountSheet}>
                  افزودن حساب
                </Button>
              </div>
              {hasAccounts && (
                <div className="item-list">
                  {accounts?.map((account) => (
                    <div key={account.id} className="finance-tx-row">
                      <span className="finance-tx-row__title">{account.name}</span>
                      <span className="finance-tx-row__amount">{formatToman(account.balanceToman)}</span>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {financeTab === "funds" && (
            <section className="section">
              <div className="finance-page__section-header">
                <p className="section__label">صندوق‌ها</p>
                <Button variant="secondary" size="sm" onClick={openFundSheet}>
                  افزودن صندوق
                </Button>
              </div>
              {funds && funds.length > 0 && (
                <div className="item-list">
                  {funds.map((fund) => (
                    <div key={fund.id} className="card">
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                        <span className="finance-tx-row__title">{fund.name}</span>
                        <span className="finance-tx-row__amount">
                          {formatToman(fund.spentToman)}
                          {fund.monthlyCapToman !== undefined && ` از ${formatToman(fund.monthlyCapToman)}`}
                        </span>
                      </div>
                      {fund.monthlyCapToman !== undefined && <Progress value={fund.spentToman} max={fund.monthlyCapToman} />}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {financeTab === "transactions" && (
            <section className="section">
              <div className="finance-page__section-header">
                <p className="section__label">تراکنش‌های اخیر</p>
                <Button variant="primary" size="sm" onClick={openTransactionSheet} disabled={!hasAccounts}>
                  ثبت تراکنش
                </Button>
              </div>
              {transactions && transactions.items.length === 0 && (
                <EmptyState message="هنوز تراکنشی برای این ماه ثبت نشده است." />
              )}
              {transactions && transactions.items.length > 0 && (
                <div className="item-list">
                  {transactions.items.map((tx) => {
                  const occurredDate = new Date(tx.occurredAt);
                  const jalali = gregorianToJalali(
                    occurredDate.getUTCFullYear(),
                    occurredDate.getUTCMonth() + 1,
                    occurredDate.getUTCDate()
                  );
                  return (
                    <button
                      key={tx.id}
                      type="button"
                      className="finance-tx-row"
                      style={{ width: "100%", font: "inherit", color: "inherit", textAlign: "inherit", cursor: "pointer" }}
                      onClick={() => openEditTransactionSheet(tx)}
                    >
                      <span>
                        <span className="finance-tx-row__title">
                          {tx.note || tx.categoryName || (tx.type === "income" ? "درآمد" : "هزینه")}
                        </span>
                        <br />
                        <span className="finance-tx-row__date">
                          {formatJalaliDateLong(jalali.year, jalali.month, jalali.day)}
                          {" · "}
                          {tx.accountName}
                          {tx.fundName ? ` · ${tx.fundName}` : ""}
                        </span>
                      </span>
                      <span
                        className={`finance-tx-row__amount ${
                          tx.type === "income" ? "finance-tx-row__amount--in" : "finance-tx-row__amount--out"
                        }`}
                      >
                        <span style={{ direction: "ltr", unicodeBidi: "isolate" }}>
                          {tx.type === "income" ? "+" : "−"}
                          {formatToman(tx.amountToman)}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            </section>
          )}

          {financeTab === "transfers" && (
            <section className="section">
              <div className="finance-page__section-header">
                <p className="section__label">انتقال‌های بین حساب‌ها</p>
                <Button variant="primary" size="sm" onClick={openTransferSheet} disabled={!accounts || accounts.length < 2}>
                  افزودن انتقال
                </Button>
              </div>
              {transfers && transfers.items.length === 0 && (
                <EmptyState message="هنوز انتقالی برای این ماه ثبت نشده است." />
              )}
              {transfers && transfers.items.length > 0 && (
                <div className="item-list">
                  {transfers.items.map((xfer) => {
                    const occurredDate = new Date(xfer.occurredAt);
                    const jalali = gregorianToJalali(
                      occurredDate.getUTCFullYear(),
                      occurredDate.getUTCMonth() + 1,
                      occurredDate.getUTCDate()
                    );
                    return (
                      <div key={xfer.id} className="finance-tx-row">
                        <span>
                          <span className="finance-tx-row__title">
                            از {xfer.fromAccountName} به {xfer.toAccountName}
                          </span>
                          <br />
                          <span className="finance-tx-row__date">
                            {formatJalaliDateLong(jalali.year, jalali.month, jalali.day)}
                            {" · "}
                            {xfer.note}
                            {xfer.fundName ? ` · ${xfer.fundName}` : ""}
                          </span>
                        </span>
                        <span className="finance-tx-row__amount">
                          <span style={{ direction: "ltr", unicodeBidi: "isolate" }}>{formatToman(xfer.amountToman)}</span>
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          )}
        </div>
      )}

      <Sheet open={sheet === "account"} onClose={() => setSheet(null)} title="افزودن حساب">
        <Field label="نام حساب">
          <Input value={accountName} onChange={(e) => setAccountName(e.target.value)} />
        </Field>
        <Field label="نوع حساب">
          <ChipGroup options={ACCOUNT_TYPE_OPTIONS} value={accountType} onChange={setAccountType} ariaLabel="نوع حساب" />
        </Field>
        <Button variant="primary" block onClick={submitAccount} disabled={busy || !accountNameValid} style={{ marginTop: 14 }}>
          ذخیره
        </Button>
      </Sheet>

      <Sheet open={sheet === "fund"} onClose={() => setSheet(null)} title="افزودن صندوق">
        <Field label="نام صندوق">
          <Input value={fundName} onChange={(e) => setFundName(e.target.value)} />
        </Field>
        <Field label="سقف ماهانه (تومان، اختیاری)">
          <Input type="number" dir="ltr" min={1} value={fundCap} onChange={(e) => setFundCap(e.target.value)} />
        </Field>
        {hasAccounts && (
          <Field label="حساب مرتبط (اختیاری)">
            <ChipGroup
              options={[{ label: "بدون حساب", value: "" }, ...(accounts ?? []).map((a) => ({ label: a.name, value: a.id }))]}
              value={fundAccountId}
              onChange={setFundAccountId}
              ariaLabel="حساب مرتبط"
            />
          </Field>
        )}
        <Button variant="primary" block onClick={submitFund} disabled={busy || !fundNameValid || !fundCapValid} style={{ marginTop: 14 }}>
          ذخیره
        </Button>
      </Sheet>

      <Sheet open={sheet === "transaction"} onClose={() => setSheet(null)} title="ثبت تراکنش مالی">
        <Field label="نوع">
          <ChipGroup options={TRANSACTION_TYPE_OPTIONS} value={txType} onChange={setTxType} ariaLabel="نوع تراکنش" />
        </Field>
        <Field label="مبلغ (تومان)">
          <Input type="number" dir="ltr" min={1} value={txAmount} onChange={(e) => setTxAmount(e.target.value)} />
        </Field>
        <Field label="تاریخ">
          <Input
            type="text"
            dir="ltr"
            placeholder="1405-06-11 یا 1405/06/11"
            value={txDate}
            onChange={(e) => setTxDate(e.target.value)}
          />
        </Field>
        {hasAccounts ? (
          <Field label="حساب">
            <ChipGroup
              options={(accounts ?? []).map((a) => ({ label: a.name, value: a.id }))}
              value={txAccountId}
              onChange={setTxAccountId}
              ariaLabel="حساب"
            />
          </Field>
        ) : (
          <p className="state-block__hint">ابتدا یک حساب ایجاد کنید.</p>
        )}
        {txEligibleFunds.length > 0 && (
          <Field label="صندوق (اختیاری)">
            <ChipGroup
              options={[{ label: "بدون صندوق", value: "" }, ...txEligibleFunds.map((f) => ({ label: f.name, value: f.id }))]}
              value={txFundId}
              onChange={setTxFundId}
              ariaLabel="صندوق"
            />
          </Field>
        )}
        <Field label="توضیحات">
          <Input value={txNote} onChange={(e) => setTxNote(e.target.value)} />
        </Field>
        <Button variant="primary" block onClick={submitTransaction} disabled={busy || !txValid} style={{ marginTop: 14 }}>
          ثبت
        </Button>
      </Sheet>

      <Sheet open={sheet === "transfer"} onClose={() => setSheet(null)} title="افزودن انتقال بین حساب‌ها">
        <Field label="از حساب">
          <ChipGroup
            options={(accounts ?? []).filter((a) => a.id !== xferToAccountId).map((a) => ({ label: a.name, value: a.id }))}
            value={xferFromAccountId}
            onChange={setXferFromAccountId}
            ariaLabel="از حساب"
          />
        </Field>
        <Field label="به حساب">
          <ChipGroup
            options={(accounts ?? []).filter((a) => a.id !== xferFromAccountId).map((a) => ({ label: a.name, value: a.id }))}
            value={xferToAccountId}
            onChange={setXferToAccountId}
            ariaLabel="به حساب"
          />
        </Field>
        <Field label="مبلغ (تومان)">
          <Input type="number" dir="ltr" min={1} value={xferAmount} onChange={(e) => setXferAmount(e.target.value)} />
        </Field>
        <Field label="تاریخ">
          <Input
            type="text"
            dir="ltr"
            placeholder="1405-06-11 یا 1405/06/11"
            value={xferDate}
            onChange={(e) => setXferDate(e.target.value)}
          />
        </Field>
        {xferEligibleFunds.length > 0 && (
          <Field label="صندوق مرتبط (اختیاری)">
            <ChipGroup
              options={[{ label: "بدون صندوق", value: "" }, ...xferEligibleFunds.map((f) => ({ label: f.name, value: f.id }))]}
              value={xferFundId}
              onChange={setXferFundId}
              ariaLabel="صندوق مرتبط"
            />
          </Field>
        )}
        <Field label="توضیحات">
          <Input value={xferNote} onChange={(e) => setXferNote(e.target.value)} />
        </Field>
        <Button variant="primary" block onClick={submitTransfer} disabled={busy || !xferValid} style={{ marginTop: 14 }}>
          ثبت انتقال
        </Button>
      </Sheet>

      <Sheet open={sheet === "editTransaction"} onClose={closeEditTransactionSheet} title="ویرایش تراکنش">
        <Field label="توضیحات">
          <Input value={editNote} onChange={(e) => setEditNote(e.target.value)} />
        </Field>
        <Field label="مبلغ (تومان)">
          <Input type="number" dir="ltr" min={1} value={editAmount} onChange={(e) => setEditAmount(e.target.value)} />
        </Field>
        <Field label="تاریخ">
          <Input
            type="text"
            dir="ltr"
            placeholder="1405-06-11 یا 1405/06/11"
            value={editDate}
            onChange={(e) => setEditDate(e.target.value)}
          />
        </Field>
        {hasAccounts ? (
          <Field label="حساب">
            <ChipGroup
              options={(accounts ?? []).map((a) => ({ label: a.name, value: a.id }))}
              value={editAccountId}
              onChange={setEditAccountId}
              ariaLabel="حساب"
            />
          </Field>
        ) : (
          <p className="state-block__hint">ابتدا یک حساب ایجاد کنید.</p>
        )}
        {editEligibleFunds.length > 0 && (
          <Field label="صندوق">
            <ChipGroup
              options={[{ label: "بدون صندوق", value: "" }, ...editEligibleFunds.map((f) => ({ label: f.name, value: f.id }))]}
              value={editFundId}
              onChange={setEditFundId}
              ariaLabel="صندوق"
            />
          </Field>
        )}
        <Button variant="primary" block onClick={submitEditTransaction} disabled={busy || !editValid} style={{ marginTop: 14 }}>
          ذخیره
        </Button>
        <Button variant="secondary" block onClick={openSplitSheet} disabled={busy} style={{ marginTop: 10 }}>
          خرد کردن تراکنش
        </Button>
      </Sheet>

      <Sheet open={sheet === "split"} onClose={closeSplitSheet} title="تقسیم تراکنش">
        <div className="finance-tx-row" style={{ marginBottom: 4 }}>
          <span className="finance-tx-row__title">مبلغ اصلی</span>
          <span className="finance-tx-row__amount">{formatToman(splitOriginalTotal)}</span>
        </div>
        <div className="finance-tx-row" style={{ marginBottom: 4 }}>
          <span className="finance-tx-row__title">مجموع فعلی</span>
          <span className="finance-tx-row__amount">{formatToman(splitCurrentTotal)}</span>
        </div>
        <div className="finance-tx-row" style={{ marginBottom: 14 }}>
          <span className="finance-tx-row__title">مابه‌التفاوت</span>
          <span
            className={`finance-tx-row__amount ${splitRemaining === 0 ? "finance-tx-row__amount--in" : "finance-tx-row__amount--out"}`}
          >
            {formatToman(splitRemaining)}
          </span>
        </div>

        {splitParts.map((part, index) => (
          <div key={index} className="card" style={{ marginBottom: 12 }}>
            <div className="finance-page__section-header">
              <p className="section__label">بخش {toPersianDigits(index + 1)}</p>
              {splitParts.length > 2 && (
                <Button variant="ghost" size="sm" onClick={() => removeSplitPart(index)}>
                  حذف
                </Button>
              )}
            </div>
            <Field label="مبلغ (تومان)">
              <Input
                type="number"
                dir="ltr"
                min={1}
                value={part.amount}
                onChange={(e) => updateSplitPartAmount(index, e.target.value)}
              />
            </Field>
            <Field label="توضیحات">
              <Input value={part.note} onChange={(e) => updateSplitPartNote(index, e.target.value)} />
            </Field>
            <Field label="دسته‌بندی">
              <ChipGroup
                options={[
                  { label: "دسته اصلی", value: INHERIT_CATEGORY },
                  { label: "بدون دسته‌بندی", value: "" },
                  ...splitCategories.map((c) => ({ label: c.name, value: c.id })),
                ]}
                value={part.categoryId}
                onChange={(value) => updateSplitPartCategory(index, value)}
                ariaLabel={`دسته‌بندی بخش ${index + 1}`}
              />
            </Field>
          </div>
        ))}

        <Button variant="secondary" block onClick={addSplitPart} style={{ marginBottom: 14 }}>
          افزودن بخش
        </Button>

        <Button variant="primary" block onClick={submitSplit} disabled={busy || !splitValid}>
          خرد کردن
        </Button>
      </Sheet>
    </section>
  );
}

export function ProjectsPage({ onNavigate }: MoreSubPageProps) {
  const { dashboard, loading, error, retry } = useDashboard();
  const projects = dashboard?.projects;

  return (
    <section>
      <BackHeader title="پروژه‌ها" onNavigate={onNavigate} />
      {loading && !dashboard && <LoadingState />}
      {error && <ErrorState message={error} onRetry={retry} />}
      {!loading && !error && projects && !projects.available && (
        <EmptyState message="این بخش هنوز روی این پایگاه‌داده راه‌اندازی نشده است." />
      )}
      {!loading && !error && projects && projects.available && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <p className="summary-label">مجموع پروژه‌ها</p>
            <p className="summary-value">{formatNumberFa(projects.total)}</p>
          </div>
          {projects.byStatus.length > 0 && (
            <div className="item-list" style={{ marginBottom: 20 }}>
              {projects.byStatus.map((entry) => (
                <div key={entry.status} className="finance-tx-row">
                  <span className="finance-tx-row__title">{statusLabel(entry.status)}</span>
                  <span className="finance-tx-row__amount">{formatNumberFa(entry.count)}</span>
                </div>
              ))}
            </div>
          )}
          <EmptyState
            message="فهرست کامل پروژه‌ها، وظایف و جزئیات هر پروژه هنوز در دسترس نیست."
            hint="فقط شمار وضعیت‌ها از داشبورد قابل نمایش است."
          />
        </>
      )}
    </section>
  );
}

export function ShoppingPage({ onNavigate }: MoreSubPageProps) {
  return (
    <section>
      <BackHeader title="لیست خرید" onNavigate={onNavigate} />
      <EmptyState
        message="این بخش هنوز در دسترس نیست."
        hint="فهرست خرید و افزودن مورد جدید هنوز به رابط برنامه وصل نشده‌اند."
      />
    </section>
  );
}
